import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';

const tzs = (n) => `TZS ${Math.round(n).toLocaleString('en-US')}`;
const grow = (amount, rate, years) => amount * (1 + rate / 100) ** years;

export const FundPicker = ({ funds, selected, onSelect }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(100000);
  const sw = language === 'sw';

  return (
    <div className="space-y-4" data-testid="utt-fund-picker">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[180px] text-xs text-muted-foreground">
          {t('Preview returns for an investment of (TZS)', 'Ona makadirio ya faida kwa uwekezaji wa (TZS)')}
          <Input type="number" min={0} value={amount} onChange={(e) => setAmount(Number(e.target.value) || 0)} className="mt-1" data-testid="utt-preview-amount" />
        </label>
        <Button variant="outline" onClick={() => navigate(`/chat?category=cis&q=${encodeURIComponent(t(`Which UTT AMIS fund suits me for TZS ${amount.toLocaleString()} and why?`, `Mfuko upi wa UTT AMIS unanifaa kwa TZS ${amount.toLocaleString()} na kwa nini?`))}`)}
          data-testid="utt-ask-ai-fund">
          <Sparkles className="mr-2 h-4 w-4" />{t('Ask AI which fund fits me', 'Muulize AI mfuko unaonifaa')}
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {funds.map((f) => {
          const on = selected === f.id;
          return (
            <button key={f.id} type="button" onClick={() => onSelect(f.id)} data-testid={`utt-fund-${f.id}`} aria-pressed={on}
              className={`rounded-2xl border p-4 text-left transition-colors duration-200 ${on ? 'border-primary bg-primary/5 shadow-sm' : 'border-border/60 hover:border-primary/30'}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{sw ? f.name_sw : f.name}</p>
                  <p className="text-xs text-muted-foreground">{sw ? f.fund_type_sw : f.fund_type} · {t('Risk', 'Hatari')} {sw ? f.risk_level_sw : f.risk_level}</p>
                </div>
                {on && <CheckCircle2 className="h-5 w-5 text-primary" />}
              </div>
              <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{sw ? f.description_sw : f.description_en}</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-muted-foreground">{t('Last 12 months', 'Miezi 12 iliyopita')}</p><p className="font-semibold text-primary">{f.annual_return}%</p></div>
                <div><p className="text-muted-foreground">{t('In 1 yr*', 'Mwaka 1*')}</p><p className="font-semibold" data-testid={`utt-fund-${f.id}-1y`}>{tzs(grow(amount, f.projection_rate ?? f.annual_return, 1))}</p></div>
                <div><p className="text-muted-foreground">{t('In 5 yrs*', 'Miaka 5*')}</p><p className="font-semibold">{tzs(grow(amount, f.projection_rate ?? f.annual_return, 5))}</p></div>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">{t('Min', 'Kiwango cha chini')} {tzs(f.min_investment)}{f.id === 'wekeza_maisha' ? t('/month', '/mwezi') : ''} · NAV {Number(f.sale_price).toLocaleString('en-US', { maximumFractionDigits: 2 })} ({f.nav_as_of})</p>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {t('*Projection uses the last 12-month return, capped at 15% a year. Bond Fund figures exclude income paid out. Past returns are not guaranteed; the unit price can go up or down. Educational only (CMSA).',
 '*Makadirio yanatumia faida ya miezi 12 iliyopita, isiyozidi 15% kwa mwaka. Takwimu za Mfuko wa Hatifungani hazijumuishi gawio lililolipwa. Faida za nyuma hazina uhakika; thamani ya kipande inaweza kuongezeka au kupungua. Elimu tu (CMSA).')}
      </p>
    </div>
  );
};
